import { PlusIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { listRolesRequest } from '@/services/roles';
import { listUsersRequest } from '@/services/users';
import type { PaginatedResult, RoleListItem } from '@/types/roles';
import type { UserListItem } from '@/types/users';

const PAGE_SIZE = 20;

type StatusFilter = 'active' | 'inactive' | 'locked' | 'all';

function isValidStatus(s: string | null): s is StatusFilter {
  return s === 'active' || s === 'inactive' || s === 'locked' || s === 'all';
}

function formatRelative(iso: string | null): string {
  if (iso === null) return 'Never';
  try {
    const date = new Date(iso);
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${String(diffMin)}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${String(diffHr)}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${String(diffDay)}d ago`;
    return date.toLocaleDateString();
  } catch {
    return iso;
  }
}

function statusVariant(status: UserListItem['status']): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case 'Active':
      return 'default';
    case 'Inactive':
      return 'secondary';
    case 'Locked':
      return 'destructive';
  }
}

export function UsersListPage(): JSX.Element {
  const { permissions } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQ = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const initialStatus: StatusFilter = isValidStatus(statusParam) ? statusParam : 'active';
  const initialRoleIdRaw = searchParams.get('roleId');
  const initialRoleId =
    initialRoleIdRaw !== null && Number.isFinite(Number(initialRoleIdRaw))
      ? Number(initialRoleIdRaw)
      : undefined;
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();

  const [q, setQ] = useState<string>(initialQ);
  const [debouncedQ, setDebouncedQ] = useState<string>(initialQ);
  const [status, setStatus] = useState<StatusFilter>(initialStatus);
  const [roleId, setRoleId] = useState<number | undefined>(initialRoleId);
  const [page, setPage] = useState<number>(initialPage);

  // 300ms debounce for search input.
  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(q);
    }, 300);
    return () => {
      window.clearTimeout(t);
    };
  }, [q]);

  // Sync URL search params when state changes.
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ !== '') next.set('q', debouncedQ);
    if (status !== 'active') next.set('status', status);
    if (roleId !== undefined) next.set('roleId', String(roleId));
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [debouncedQ, status, roleId, page, setSearchParams]);

  // Reset to page 1 when filters change.
  useEffect(() => {
    setPage(1);
  }, [debouncedQ, status, roleId]);

  const usersQuery = useQuery<PaginatedResult<UserListItem>, ApiError>({
    queryKey: ['users', debouncedQ, status, roleId, page, PAGE_SIZE],
    queryFn: () => {
      const query = {
        q: debouncedQ,
        status,
        page,
        pageSize: PAGE_SIZE,
        ...(roleId !== undefined ? { roleId } : {}),
      };
      return listUsersRequest(query);
    },
  });

  const rolesQuery = useQuery<PaginatedResult<RoleListItem>>({
    queryKey: ['roles', 'active', 100],
    queryFn: () => listRolesRequest({ status: 'active', page: 1, pageSize: 100 }),
  });

  const columns = useMemo<ColumnDef<UserListItem>[]>(
    () => [
      {
        accessorKey: 'fullName',
        header: 'Full Name',
        cell: ({ row }) => (
          <Link
            to={`/users/${String(row.original.id)}`}
            className="text-primary hover:underline"
            data-testid={`users-row-name-${String(row.original.id)}`}
          >
            {row.original.fullName}
          </Link>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'employeeId',
        header: 'Employee ID',
        cell: ({ row }) => row.original.employeeId ?? '—',
      },
      {
        accessorKey: 'roleName',
        header: 'Role',
        cell: ({ row }) =>
          row.original.roleName !== null ? (
            <Badge variant="outline">{row.original.roleName}</Badge>
          ) : (
            '—'
          ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>
        ),
      },
      {
        accessorKey: 'lastLoginAt',
        header: 'Last Login',
        cell: ({ row }) => formatRelative(row.original.lastLoginAt),
      },
    ],
    [],
  );

  const data = usersQuery.data?.items ?? [];
  const total = usersQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const tableData = data as unknown as UserListItem[];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('users.read')) {
    return <Navigate to="/dashboard" replace />;
  }

  const canCreate = permissions.includes('users.create');
  const roleOptions = rolesQuery.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Users</h1>
          <p className="text-muted-foreground">Manage users, roles, and access state.</p>
        </div>
        {canCreate && (
          <Button asChild data-testid="users-add-button">
            <Link to="/users/new">
              <PlusIcon size={16} weight="bold" /> Add User
            </Link>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
          <CardDescription>
            Search by name, email, or employee ID. Filter by role and status.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px] space-y-1">
              <Label htmlFor="users-search">Search</Label>
              <Input
                id="users-search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                }}
                placeholder="Name, email, or employee ID…"
                data-testid="users-search-input"
              />
            </div>
            <div className="w-48 space-y-1">
              <Label htmlFor="users-role">Role</Label>
              <Select
                value={roleId === undefined ? 'all' : String(roleId)}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setRoleId(undefined);
                  } else {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) setRoleId(n);
                  }
                }}
              >
                <SelectTrigger id="users-role" data-testid="users-role-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="users-role-all">
                    All roles
                  </SelectItem>
                  {roleOptions.map((r) => (
                    <SelectItem
                      key={r.id}
                      value={String(r.id)}
                      data-testid={`users-role-option-${String(r.id)}`}
                    >
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40 space-y-1">
              <Label htmlFor="users-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => {
                  if (isValidStatus(v)) setStatus(v);
                }}
              >
                <SelectTrigger id="users-status" data-testid="users-status-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active" data-testid="users-status-active">
                    Active
                  </SelectItem>
                  <SelectItem value="inactive" data-testid="users-status-inactive">
                    Inactive
                  </SelectItem>
                  <SelectItem value="locked" data-testid="users-status-locked">
                    Locked
                  </SelectItem>
                  <SelectItem value="all" data-testid="users-status-all">
                    All
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {usersQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="users-loading">
              Loading users…
            </p>
          )}
          {usersQuery.isError && (
            <p className="text-sm text-destructive" data-testid="users-error">
              Failed to load users.
            </p>
          )}
          {usersQuery.data && (
            <>
              <div className="overflow-x-auto">
              <Table data-testid="users-table">
                <TableHeader>
                  {table.getHeaderGroups().map((hg) => (
                    <TableRow key={hg.id}>
                      {hg.headers.map((h) => (
                        <TableHead key={h.id}>
                          {h.isPlaceholder
                            ? null
                            : flexRender(h.column.columnDef.header, h.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="text-center py-8">
                        <span data-testid="users-empty">No users found.</span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id} data-testid={`users-row-${String(row.original.id)}`}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-muted-foreground" data-testid="users-pagination-summary">
                  Page {page} of {pageCount} • {total} total
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={page <= 1}
                    data-testid="users-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.min(pageCount, p + 1));
                    }}
                    disabled={page >= pageCount}
                    data-testid="users-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
